import React from 'react';
import styled, { keyframes } from 'styled-components';

const SoftwareIdealClient = () => {
  return (
    <IdealClientSection id="ideal-client">
      <SectionTitle>Is This Right For You?</SectionTitle>
      
      <ClientColumns>
        <ClientColumn isPositive={true}>
          <ColumnTitle>
            <IconWrapper isPositive={true}>✓</IconWrapper>
            Perfect For People Who:
          </ColumnTitle>
          
          <ClientList>
            <ClientItem>
              <ClientItemTitle>Want to win at business</ClientItemTitle>
              <ClientItemText>And have secretly practiced their "How did we grow so fast?" humble-brag face in the mirror.</ClientItemText>
            </ClientItem>
            
            <ClientItem>
              <ClientItemTitle>Want Their Business to Level Up</ClientItemTitle>
              <ClientItemText>Because "plateau" sounds like a fancy dessert, not a business strategy.</ClientItemText>
            </ClientItem>
            
            <ClientItem>
              <ClientItemTitle>Have Ideas Worth Millions</ClientItemTitle>
              <ClientItemText>But need someone who can turn "wouldn't it be cool if..." into actual working software.</ClientItemText>
            </ClientItem>
            
            <ClientItem>
              <ClientItemTitle>Prefer Money-Making Over Mouse-Clicking</ClientItemTitle>
              <ClientItemText>And want AI to handle the repetitive tasks while they focus on yacht shopping.</ClientItemText>
            </ClientItem>
            
            <ClientItem>
              <ClientItemTitle>Want to Use AI Before It's Mainstream</ClientItemTitle>
              <ClientItemText>So they can say "Oh, we've been using that since 2025" when everyone else jumps on the bandwagon.</ClientItemText>
            </ClientItem>
          </ClientList>
        </ClientColumn>
        
        <ClientColumn isPositive={false}>
          <ColumnTitle>
            <IconWrapper isPositive={false}>✗</IconWrapper>
            Not Ideal For:
          </ColumnTitle>
          
          <ClientList>
            <ClientItem>
              <ClientItemTitle>AI Skeptics</ClientItemTitle>
              <ClientItemText>Those who don't yet recognize the transformative potential of AI technologies.</ClientItemText>
            </ClientItem>
            
            <ClientItem>
              <ClientItemTitle>Non-humans</ClientItemTitle>
              <ClientItemText>If you are not human then this doesn't apply to you, sorry.</ClientItemText>
            </ClientItem>
            
            <ClientItem>
              <ClientItemTitle>People that don't want to buy it</ClientItemTitle>
              <ClientItemText>Hey this is just an ad, not an obligation, take it easy. Also you can message us directly at info@bbglobalsolutions.org</ClientItemText>
            </ClientItem>
            
            <ClientItem>
              <ClientItemTitle></ClientItemTitle>
              <ClientItemText></ClientItemText>
            </ClientItem>
            
            <ClientItem>
              <ClientItemTitle></ClientItemTitle>
              <ClientItemText></ClientItemText>
            </ClientItem>
          </ClientList>
        </ClientColumn>
      </ClientColumns>
      
      <BottomCTA>
        <CTAButton href="#contact">Let's Transform Your Business</CTAButton>
      </BottomCTA>
    </IdealClientSection>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const IdealClientSection = styled.section`
  padding: 80px 60px;
  background-color: #f0f9ff;
  
  @media (max-width: 991px) {
    padding: 60px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 50px 20px;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
`;

const ClientColumns = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 40px;
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 991px) {
    flex-direction: column;
    gap: 25px;
  }
`;

const ClientColumn = styled.div`
  flex: 1;
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border-top: 6px solid ${({ isPositive }) => isPositive ? '#1a95c9' : '#FF4757'};
  
  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const ColumnTitle = styled.h3`
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ isPositive }) => isPositive ? '#1a95c9' : '#FF4757'};
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  margin-right: 12px;
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 1rem;
    margin-right: 10px;
  }
`;

const ClientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  
  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const ClientItem = styled.div`
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 18px;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding-bottom: 15px;
  }
`;

const ClientItemTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ClientItemText = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const BottomCTA = styled.div`
  text-align: center;
  animation: ${fadeIn} 1s ease-out;
`;

const CTAButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #1a95c9, #39b9ef);
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 14px 35px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(26, 149, 201, 0.2);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 25px rgba(26, 149, 201, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 12px 28px;
  }
`;

export default SoftwareIdealClient; 